// Face check-in endpoints (user-service, self-only unless noted).
//
//   GET  /attendance/face/status      → { mode: OFF|OPTIONAL|REQUIRED, consented, enrolled, modelVersionOk }
//   POST /attendance/face/consent
//   POST /attendance/face/enroll      multipart "images" ×3     422 {code,message} on a rejected shot
//   GET  /attendance/face/challenge   → { challenge: "left-right"|…, steps[], nonce, expiresAt }
//        one random head-turn order per attempt, valid 60 s, single use
//   POST /attendance/face/check-in    multipart "frames" ×6 + type in|out + challenge + nonce
//        200 attendance (faceFlag "service_down" when the face service was
//        unreachable and the punch fell back to PIN), 403/422 {code,message},
//        409 when re-enrollment is required
//   POST /attendance/face/:infinityCode/reset   (HR admin)
//
// Every failure is thrown as FaceApiError { status, code, message, body } so
// the UI can map `code` to a translated string and still show the server's
// own message verbatim.
import { apiFetch } from "../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || "";

export const FACE_MODES = { OFF: "OFF", OPTIONAL: "OPTIONAL", REQUIRED: "REQUIRED" };

export class FaceApiError extends Error {
  constructor(status, body, fallbackMessage) {
    super(body?.message || body?.error || fallbackMessage || `Request failed (${status})`);
    this.name = "FaceApiError";
    this.status = status;
    this.code = body?.code || (status === 409 ? "reenrol_required" : null);
    this.body = body;
    // Optional hint from the detector: where the face box was, if the server sent it.
    this.faceBox = body?.faceBox || body?.face_box || body?.data?.faceBox || null;
  }
}

async function parse(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) throw new FaceApiError(res.status, body);
  return body?.data !== undefined && body?.data !== null && typeof body.data === "object" ? { ...body, ...body.data } : body;
}

const normalizeMode = (m) => {
  const v = String(m || "OFF").toUpperCase();
  return FACE_MODES[v] ? v : FACE_MODES.OFF;
};

export const faceApi = {
  /** Never throws on a missing endpoint: an old server means "OFF". */
  status: async () => {
    try {
      const data = await parse(await apiFetch(`${BASE_URL}/attendance/face/status`));
      return {
        mode: normalizeMode(data.mode),
        consented: Boolean(data.consented),
        enrolled: Boolean(data.enrolled),
        modelVersionOk: data.modelVersionOk !== false,
        canEnrol: data.canEnrol,
        enrolledAt: data.enrolledAt || null,
        // false while the face service's /health is failing (the server then
        // lets REQUIRED companies punch the regular way, flagged service_down)
        serviceAvailable: data.serviceAvailable !== false,
        keyConfigured: data.keyConfigured !== false,
        available: true,
      };
    } catch (err) {
      if (err instanceof FaceApiError && (err.status === 404 || err.status === 501)) {
        return { mode: FACE_MODES.OFF, consented: false, enrolled: false, modelVersionOk: true, available: false };
      }
      throw err;
    }
  },

  consent: () => apiFetch(`${BASE_URL}/attendance/face/consent`, { method: "POST" }).then(parse),

  /**
   * The head-turn order for the next check-in attempt. The modal shows the
   * steps as prompts in this order and echoes `challenge` + `nonce` with the
   * frames; the server refuses frames without a live nonce.
   */
  challenge: async () => {
    const data = await parse(await apiFetch(`${BASE_URL}/attendance/face/challenge`));
    const challenge = String(data.challenge || "").toLowerCase();
    const steps = Array.isArray(data.steps) && data.steps.length ? data.steps.map((s) => String(s).toLowerCase()) : challenge.split("-").filter(Boolean);
    return { challenge, steps, nonce: data.nonce || null, expiresAt: data.expiresAt || null };
  },

  /** images: Blob[3] (JPEG). */
  enroll: (images) => {
    const fd = new FormData();
    images.forEach((blob, i) => fd.append("images", blob, `enrol-${i + 1}.jpg`));
    return apiFetch(`${BASE_URL}/attendance/face/enroll`, { method: "POST", body: fd }).then(parse);
  },

  /** frames: Blob[6] (JPEG); type: "in" | "out"; extra: { challenge, nonce } from `challenge()` (and anything else) merged into the body. */
  checkIn: (frames, type, extra = {}) => {
    const fd = new FormData();
    frames.forEach((blob, i) => fd.append("frames", blob, `frame-${i + 1}.jpg`));
    fd.append("type", type);
    Object.entries(extra).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      fd.append(k, typeof v === "object" ? JSON.stringify(v) : String(v));
    });
    return apiFetch(`${BASE_URL}/attendance/face/check-in`, { method: "POST", body: fd }).then(parse);
  },

  /**
   * After `retry_exhausted`: the server-validated fallback punch (no frames).
   * The server only accepts it with two recent identity rejections on file.
   */
  fallback: (type) => faceApi.checkIn([], type, { fallback: true }),

  /** HR admin: per working company, may the Reset button be shown? → { companies: [{ workingCompany, switchOn, mode, enabled }] } */
  adminAvailability: () => apiFetch(`${BASE_URL}/attendance/face/admin/availability`, { method: "GET" }).then(parse),

  /** HR admin: forget the employee's templates so they enroll again. */
  reset: (infinityCode) =>
    apiFetch(`${BASE_URL}/attendance/face/${encodeURIComponent(infinityCode)}/reset`, { method: "POST" }).then(parse),
};

export default faceApi;
